import { type CSSProperties, type FC } from 'react';
import { Badge, Blockquote, Card, Flex, LoadingOverlay, Pagination, Stack, Text } from '@mantine/core';
import { useUnit } from 'effector-react';
import cn from 'classnames';
import {
    formatTicket,
    payinTicketPriorityColors,
    payinTicketStatusColors,
    usePayinTicketsList,
    type PayinTicket,
    type PayinTicketFilters,
} from '~/entities/payin';
import { type PaginationModel } from '~/shared/lib/pagination';
import { KeyValuePair } from '~/shared/ui/KeyValuePair';
import { type AdminPayinTicketServiceListTicketsFilterPrioritiesItem } from '~/generated/payinBackend/model';
import s from './style.module.css';
import { openTicket } from './model';

interface TicketsListProps {
    polling?: boolean;
    filter: PayinTicketFilters;
    paginationModel: PaginationModel;
    onClick?: (ticket: PayinTicket) => void;
    direction?: CSSProperties['flexDirection'];
    itemMaw?: CSSProperties['maxWidth'];
}

export const TicketsList: FC<TicketsListProps> = ({
    filter = {},
    paginationModel,
    onClick,
    direction = 'column',
    itemMaw = '100%',
}) => {
    const { pagination } = useUnit({
        pagination: paginationModel.$pagination,
    });

    const { data: { items, pagination: paginationMeta } = {}, isPending } = usePayinTicketsList({
        'filter.statuses': filter.statuses,
        'filter.priorities': filter.priorities as unknown as AdminPayinTicketServiceListTicketsFilterPrioritiesItem[],
        'pagination.page': pagination.page.toString(),
        'pagination.perPage': pagination.perPage.toString(),
    });

    const total = Number(paginationMeta?.total);
    const totalPages = Math.ceil(total / pagination.perPage);

    const isRow = direction === 'row';

    const handleChangeCurrentPage = (page: number) => {
        paginationModel.setPage(page - 1);
    };

    return (
        <Flex
            gap='md'
            wrap='wrap'
            align='stretch'
            direction={direction}
        >
            <LoadingOverlay visible={isPending} />

            {!items?.length && !isPending && (
                <Text
                    ta='center'
                    w='100%'
                >
                    Нет новых заявок.
                </Text>
            )}

            {items?.map((t) => (
                <Card
                    key={t.id?.value}
                    p={10}
                    withBorder
                    maw={itemMaw}
                    // eslint-disable-next-line react/forbid-component-props
                    style={
                        isRow
                            ? {
                                  flex: '1 1 0',
                                  minWidth: 260,
                              }
                            : undefined
                    }
                    onClick={() => {
                        openTicket(t);
                        onClick?.(t);
                    }}
                    className={cn(s.pointer)}
                >
                    <Card.Section
                        withBorder
                        inheritPadding
                        py={10}
                    >
                        <KeyValuePair
                            label='ID'
                            value={<Text>{t.id?.value || '-'}</Text>}
                            direction='row'
                        />
                    </Card.Section>

                    <Stack gap='xs'>
                        <Blockquote
                            mx={-10}
                            radius={0}
                            p='xs'
                            className={s.message}
                        >
                            {t.message}
                        </Blockquote>

                        {t.status && (
                            <KeyValuePair
                                label='Статус'
                                direction='row'
                                value={
                                    <Badge
                                        className={cn({ [s.pointer]: onClick })}
                                        color={payinTicketStatusColors[t.status]}
                                        fz='xs'
                                    >
                                        {formatTicket.status(t.status)}
                                    </Badge>
                                }
                            />
                        )}

                        {t.priority && (
                            <KeyValuePair
                                label='Приоритет'
                                direction='row'
                                value={
                                    <Badge
                                        className={cn({ [s.pointer]: onClick })}
                                        color={payinTicketPriorityColors[t.priority]}
                                        fz='xs'
                                    >
                                        {formatTicket.priority(t.priority)}
                                    </Badge>
                                }
                            />
                        )}

                        {t.createdAt && (
                            <KeyValuePair
                                label='Дата создания'
                                value={formatTicket.date(t.createdAt)}
                                direction='row'
                            />
                        )}
                    </Stack>
                </Card>
            ))}

            {totalPages > 1 && (
                <Pagination
                    total={totalPages}
                    value={pagination.page + 1}
                    onChange={handleChangeCurrentPage}
                    size='sm'
                />
            )}
        </Flex>
    );
};
